import {isAxiosError} from 'axios'
import i18next from 'i18next'
import {toast} from '@utilities/toast/toast.tsx'
import {forwardRef, ForwardRefExoticComponent, PropsWithoutRef, ReactNode, Ref, RefAttributes} from 'react'
import {OptionsType} from '@/types.ts'

export const capitalize = (s: string) => s?.charAt(0)?.toUpperCase() + s?.slice(1)

export const megabytesToBytes = (megabytes: number) => megabytes * 1_000_000

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
    throw new Error(error)
}

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
    callback: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            callback(...args)
        }, delay)
    }
}

export const errorHandler = (error: unknown) => {
    if (isAxiosError(error)) {
        const message =
            error.response?.data.message && error.status != 500
                ? i18next.t(`errors:${error.response.data.message}`)
                : i18next.t('errors:default')
        return toast.error(message)
    }

    toast.error(i18next.t('errors:default'))
}

export const ObjectKeys = Object.keys as <T extends object>(object: T) => Array<keyof T>
export const ObjectValues = Object.values as <T extends object>(object: T) => Array<T[keyof T]>
export const ObjectEntries = Object.entries as <T extends object>(
    object: T
) => Array<{[K in keyof T]: [K, T[K]]}[keyof T]>

export function genericForwardRef<TRef, TProps extends object>(
    render: (props: PropsWithoutRef<TProps>, ref: Ref<TRef>) => ReactNode
): ForwardRefExoticComponent<PropsWithoutRef<TProps> & RefAttributes<TRef>> {
    return forwardRef<TRef, TProps>(render)
}

export const highlightText = (text: string, query: string) => {
    if (!query) return text

    const regex = new RegExp(`(${query})`, 'gi')
    return text.replace(regex, `<mark>$1</mark>`)
}

export const trimString = (str: string, maxLength: number) => {
    return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str
}
export const remapFiltersOptions = (options: OptionsType['data']) => {
    if (!options || options?.length === 0) return []

    return (options as {id: string; name?: string; keyName?: string; keyTarName?: string}[])?.map(option => {
        const name = (option.name ?? option.keyName ?? option.keyTarName ?? option.id).replace(/_/g, ' ')
        return {
            id: option.id,
            name: capitalize(name)
        }
    })
}

export const formatGeneralRoleNumber = (roleNumber?: string | undefined) => {
    if (!roleNumber) return
    const year = roleNumber.slice(0, 4)
    const number = roleNumber.slice(4)
    return `${number}/${year}`
}

export const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
}

export async function transformXml(xmlString: string, measureType: string = 'default'): Promise<string> {
    // Select appropriate XSL based on measure type
    let xsltPath = '/Parere.xsl' // Default XSL

    switch (measureType) {
        case 'judgment':
        case 'short_judgment':
        case 'judgment_device':
            xsltPath = '/Sentenze.xsl'
            break
        case 'precautionary_decree':
        case 'collegiate_decree':
        case 'dispositive_decree':
        case 'injunction_decree':
        case 'presidential_decree':
        case 'legal_aid':
            xsltPath = '/Decreti.xsl'
            break
        case 'precautionary_order':
        case 'collegial_order':
        case 'injunction_order':
        case 'presidential_order':
            xsltPath = '/Ordinanze.xsl'
            break
        case 'opinion':
        case 'interlocutory_opinion':
        case 'final_opinion':
        case 'suspensive_opinion':
        default:
            xsltPath = '/Parere.xsl'
            break
    }

    try {
        // Fetch the appropriate XSL file
        const response = await fetch(xsltPath)
        const xsltString = await response.text()

        const parser = new DOMParser()
        const xsltProcessor = new window.XSLTProcessor()

        const xsltDoc = parser.parseFromString(xsltString, 'application/xml')
        const xmlDoc = parser.parseFromString(xmlString, 'application/xml')

        xsltProcessor.importStylesheet(xsltDoc)

        xsltProcessor.setParameter(
            null,
            'create-date',
            new Intl.DateTimeFormat('en-US', {
                dateStyle: 'full',
                timeStyle: 'long'
            }).format(new Date())
        )

        const resultDocument = xsltProcessor.transformToDocument(xmlDoc)

        const string = new XMLSerializer().serializeToString(resultDocument)

        return string.replace('<head>', '<head><base href="/">')
    } catch (error) {
        console.error(`Error transforming XML: ${error}`)
        return xmlString
    }
}
